import { diffWords, type TextDiffSegment } from "@/lib/textDiff";
import { cn } from "@/lib/utils";
import { ItemHistoryChangeValues } from "./ItemHistoryChangeValues";
import type { ItemHistoryDetail } from "./itemHistoryRows";

// Per edit type: the glyph, its tone, and the diff band behind the line.
const VERBS = {
  added: {
    glyph: "+",
    tone: "text-success-foreground",
    band: "bg-success/10",
    hoverBand: "hover:bg-success/20",
  },
  removed: {
    glyph: "−",
    tone: "text-danger-foreground",
    band: "bg-danger/10",
    hoverBand: "hover:bg-danger/20",
  },
  changed: {
    glyph: "~",
    tone: "text-warning-foreground",
    band: "bg-warning/10",
    hoverBand: "hover:bg-warning/20",
  },
} as const;
const SEGMENT_CLASSES = {
  same: "",
  removed: "rounded-sm bg-danger/15 px-0.5 text-muted-foreground line-through",
  added: "rounded-sm bg-success/15 px-0.5",
} as const;

/**
 * One line of an event's detail: an added, removed, or changed entry, a
 * starting value, a text version, or a group heading.
 * @param line - The line to render
 * @param onSelect - Jumps the form to the entry's BOM row, when it has one
 */
export const ItemHistoryDetailLine = ({
  line,
  onSelect,
}: {
  line: ItemHistoryDetail;
  onSelect?: () => void;
}) => {
  if (line.type === "group") {
    return (
      <p className="pb-px pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {line.label}
      </p>
    );
  }
  if (line.type === "value") {
    return (
      <div className="grid grid-cols-[minmax(0,1fr)_fit-content(55%)] gap-x-4 text-sm leading-6 tabular-nums">
        <span className="break-words text-muted-foreground">{line.label}</span>
        <span className="min-w-0 whitespace-pre-wrap text-right font-medium [overflow-wrap:anywhere]">
          {line.value}
        </span>
      </div>
    );
  }
  if (line.type === "text") {
    const segments: TextDiffSegment[] =
      line.from === null
        ? [{ type: "same", text: line.to }]
        : diffWords(line.from, line.to);
    return (
      <p className="whitespace-pre-wrap text-sm leading-6">
        {segments.map((segment, index) => (
          <DiffSegment key={index} segment={segment} />
        ))}
      </p>
    );
  }

  const verb = VERBS[line.type];
  // The negative margin widens the band past the text without moving the
  // glyph column off the rows above.
  const classes = cn(
    "-mx-1.5 grid w-[calc(100%+0.75rem)] grid-cols-[14px_minmax(0,1fr)_fit-content(55%)] items-baseline gap-x-2 rounded-sm px-1.5 text-left text-sm leading-6",
    verb.band,
  );
  const body = (
    <>
      <span className={cn("text-center", verb.tone)}>{verb.glyph}</span>
      <span
        className={cn("break-words", line.type !== "changed" && "col-span-2")}
      >
        <span className="font-semibold">{line.name}</span>
        {line.type === "changed" && line.field ? (
          <span className="text-muted-foreground"> · {line.field}</span>
        ) : null}
        {line.type === "added" && line.facts ? (
          <span className="text-muted-foreground"> · {line.facts}</span>
        ) : null}
      </span>
      {line.type === "changed" ? (
        <span className="min-w-0 pl-2 text-right tabular-nums">
          <ItemHistoryChangeValues from={line.from} to={line.to} wrap />
        </span>
      ) : null}
    </>
  );
  if (!onSelect) {
    return <div className={classes}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        classes,
        verb.hoverBand,
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {body}
    </button>
  );
};

// The tint covers the word only; its trailing space stays plain between words.
const DiffSegment = ({ segment }: { segment: TextDiffSegment }) => {
  const [, word, space] = /^([\s\S]*?)(\s*)$/.exec(segment.text) ?? [
    "",
    segment.text,
    "",
  ];
  return (
    <>
      {word ? (
        <span className={SEGMENT_CLASSES[segment.type]}>{word}</span>
      ) : null}
      {space}
    </>
  );
};
